import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  useLocation,
  ScrollRestoration,
  Outlet,
} from "react-router-dom";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import { ThemeProvider } from "./theme/index.js";
import { activeBrand } from "./theme/brands/index.js";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/de";
import { SnackbarProvider } from "notistack";
import LoginPage from "./pages/LoginPage.jsx";
import { HomePage } from "./pages/HomePage.jsx";
import ClaimIndexPage from "./pages/claims/ClaimIndexPage.jsx";
import ClaimShowPage from "./pages/claims/ClaimShowPage.jsx";
import { UserProvider } from "./contexts/UserContextProvider.jsx";
import { BreadcrumbProvider } from "./contexts/BreadcrumbContext.jsx";
import ProtectedRoute from "./routes/ProtectedRoute.jsx";
import RouteErrorBoundary from "./routes/RouteErrorBoundary.jsx";
import BackendLayout from "./layouts/BackendLayout.jsx";
import ClaimCreatePage from "./pages/claims/ClaimCreatePage.jsx";
import ContractIndexPage from "./pages/contracts/ContractIndexPage.jsx";
import ObjectIndexPage from "./pages/objects/ObjectIndexPage.jsx";
import UserIndexPage from "./pages/users/UserIndexPage.jsx";
import UserDetailPage from "./pages/users/UserDetailPage.jsx";
import MailMatchingPage from "./pages/mail/MailMatchingPage.jsx";
import PaymentMatchingPage from "./pages/bookings/PaymentMatchingPage.jsx";
import { SubstitutionsPage } from "./pages/SubstitutionsPage.jsx";
import { NotificationsPage } from "./pages/NotificationsPage.jsx";
import ClaimStatusReminderAdministrationPage from "./pages/admin/ClaimStatusReminderAdministrationPage.jsx";
import BookingIndexPage from "./pages/admin/bookings/BookingIndexPage.jsx";
import PaymentImportShowPage from "./pages/admin/bookings/PaymentImportShowPage.jsx";
import CustomerIndexPage from "./pages/admin/customers/CustomerIndexPage.jsx";
import CustomerShowPage from "./pages/admin/customers/CustomerShowPage.jsx";
import UserAccountSettingsPage from "./pages/account/UserAccountSettingsPage.jsx";
import KitchenSinkFormPage from "./pages/dev/KitchenSinkFormPage.jsx";
import { useEffect, useMemo } from "react";
import BankAccountsAdministrationPage from "./pages/admin/BankAccountsAdministrationPage.jsx";
import ControllingIndexPage from "./pages/controlling/ControllingIndexPage.jsx";
import InsurerIndexPage from "./pages/admin/insurers/InsurerIndexPage.jsx";

function NotFoundRedirect() {
  console.error("Route nicht gefunden:", window.location.pathname);
  return <Navigate to="/login" replace />;
}

function SchadenRedirect() {
  const location = useLocation();

  const newPath = location.pathname.replace(/^\/schaden/, "/schaeden");

  return (
    <Navigate
      to={`${newPath}${location.search ?? ""}${location.hash ?? ""}`}
      replace
    />
  );
}

function RootRouteElement() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

/**
 * Main App Component for v2 Design
 * This is the redesigned version of the application
 */
export default function App() {
  useEffect(() => {
    // Title + favicon come from the active skin (theme/brands). The title
    // matches the footer product name; the favicon is optional — a skin may
    // omit it, in which case we leave the default favicon untouched.
    document.title =
      activeBrand.footer?.productName ??
      activeBrand.productName ??
      "Schadenportal";

    const favicon = activeBrand.assets?.favicon;
    if (!favicon) {
      return undefined;
    }

    const link = document.createElement("link");
    link.rel = "icon";
    link.href = favicon;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const router = useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route
          element={<RootRouteElement />}
          errorElement={<RouteErrorBoundary />}
        >
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute />}>
            {/* Default backend layout with standard max width */}
            <Route
              element={<BackendLayout />}
              errorElement={<RouteErrorBoundary />}
            >
              <Route index element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/schaeden/:schaden_id" element={<ClaimShowPage />} />
              <Route path="/schaden/*" element={<SchadenRedirect />} />
              <Route
                path="/schaeden/neu/:vertrag_id/:objekt_id"
                element={<ClaimCreatePage />}
              />
              <Route
                path="/schaeden/:schaden_id/kommunikation/:kommunikation_id"
                element={<ClaimShowPage />}
              />
              <Route
                path="/schaden/:schaden_id/kommunikation/:kommunikation_id"
                element={
                  <Navigate
                    replace
                    to={({ params }) =>
                      `/schaeden/${params.schaden_id}/kommunikation/${params.kommunikation_id}`
                    }
                  />
                }
              />

              <Route
                path="/admin/bankaccounts"
                element={<BankAccountsAdministrationPage />}
              />

              <Route path="/admin/vertraege" element={<ContractIndexPage />} />
              <Route path="/admin/objekte" element={<ObjectIndexPage />} />
              <Route path="/admin/users" element={<UserIndexPage />} />
              <Route
                path="/admin/users/assignments"
                element={<UserIndexPage />}
              />
              <Route path="/admin/users/:userId" element={<UserDetailPage />} />
              <Route
                path="/admin/emails/:subFolder?/:mailId?"
                element={<MailMatchingPage />}
              />
              <Route
                path="/admin/reminders"
                element={<ClaimStatusReminderAdministrationPage />}
              />

              <Route
                path="/admin/buchungen/:activeTab?"
                element={<BookingIndexPage />}
              />

              <Route
                path="/admin/buchungen/imports/:id"
                element={<PaymentImportShowPage />}
              />

              <Route path="/substitutions" element={<SubstitutionsPage />} />

              <Route path="/notifications" element={<NotificationsPage />} />

              <Route path="/admin/kunden" element={<CustomerIndexPage />} />
              <Route
                path="/admin/kunden/:customerId"
                element={<CustomerShowPage />}
              />

              <Route path="/admin/insurers" element={<InsurerIndexPage />} />

              <Route
                path="/account/:section?"
                element={<UserAccountSettingsPage />}
              />

              {process.env.NODE_ENV !== "production" && (
                <>
                  <Route
                    path="/dev/forms-kitchen-sink"
                    element={<KitchenSinkFormPage />}
                  />
                </>
              )}
            </Route>

            {/* wide-layout backend for pages that need more space (full-bleed) */}
            <Route
              element={<BackendLayout />}
              errorElement={<RouteErrorBoundary />}
            >
              <Route
                path="/admin/buchungen/imports/:id/payment-matching"
                element={<PaymentMatchingPage />}
              />
              <Route path="/controlling" element={<ControllingIndexPage />} />
            </Route>

            {/* full-bleed backend for the claims overview (matches the dummy) */}
            <Route
              element={<BackendLayout maxContentWidth="100%" />}
              errorElement={<RouteErrorBoundary />}
            >
              <Route path="/schaeden" element={<ClaimIndexPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFoundRedirect />} />
        </Route>
      )
    );
  }, []);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <UserProvider>
            <BreadcrumbProvider>
              <RouterProvider router={router} />
            </BreadcrumbProvider>
          </UserProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
